<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const settings = ref({
  companyName: 'ARK Gaming Ltd',
  regNumber: '12345678',
  address: '123 Gaming Street',
  city: 'London, United Kingdom',
  supportEmail: 'support@arkserver.com',
  discordLink: '',
  facebookLink: 'https://facebook.com',
  instagramLink: 'https://instagram.com',
  copyright: '© 2026 ARK Gaming Ltd. All rights reserved. This server is not affiliated with, endorsed, or sponsored by the official game developers of ARK (Studio Wildcard).'
})

const legalPages = ref([])
const paymentLogos = ref([])

const displayCopyright = computed(() => {
  const text = settings.value.copyright || ''
  if (!text) return ''
  return text.replace(/\b20\d{2}\b/g, String(new Date().getFullYear()))
})

onMounted(async () => {
  try {
    const response = await axios.get(`${API_URL}/api/settings`)
    if (response.data) {
      settings.value = { ...settings.value, ...response.data }
    }
  } catch (e) {}

  try {
    const response = await axios.get(`${API_URL}/api/pages`)
    if (Array.isArray(response.data)) {
      legalPages.value = response.data
    }
  } catch (e) {}

  try {
    const response = await axios.get(`${API_URL}/api/payments/footer-logos`)
    if (Array.isArray(response.data?.logos)) {
      paymentLogos.value = response.data.logos
    }
  } catch (e) {}
})
</script>

<template>
  <footer class="footer">
    <div class="footer-main">
      <div class="footer-inner">
        <!-- Company Info -->
        <div class="footer-company">
          <p class="company-name">{{ settings.companyName }}</p>
          <p class="company-detail">Reg.Code: {{ settings.regNumber }}</p>
          <p class="company-detail">Address: {{ settings.address }},</p>
          <p class="company-detail">{{ settings.city }}</p>
        </div>

        <!-- Logo -->
        <div class="footer-logo">
          <img src="/logo.png?v=2" alt="ARK Server" class="footer-logo-img" />
        </div>

        <!-- Contact & Social -->
        <div class="footer-contact">
          <a :href="'mailto:' + settings.supportEmail" class="contact-email">{{ settings.supportEmail }}</a>
          <div class="social-links">
            <a :href="settings.facebookLink || 'https://facebook.com'" target="_blank" class="social-icon" title="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a :href="settings.instagramLink || 'https://instagram.com'" target="_blank" class="social-icon" title="Instagram">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer-bottom">
      <div class="footer-bottom-inner">
        <p class="copyright">{{ displayCopyright }}</p>
        <div class="footer-bottom-right">
          <div class="legal-links">
            <RouterLink v-for="page in legalPages" :key="page.slug" :to="`/page/${page.slug}`">{{ page.title }}</RouterLink>
          </div>
          <div class="payment-icons">
            <div class="payment-logos-group">
              <img
                v-for="(pl, idx) in paymentLogos"
                :key="pl.logo + idx"
                :src="pl.logo"
                :alt="pl.name || 'Payment method'"
                class="payment-img"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  background: rgba(0, 0, 0, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.footer-main {
  padding: 40px 60px;
}

.footer-inner {
  max-width: var(--container-max);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 40px;
}

/* Company Info */
.footer-company {
  text-align: left;
}

.company-name {
  color: #e0e0e0;
  font-size: 0.95rem;
  margin-bottom: 6px;
  font-weight: 600;
}

.company-detail {
  color: #aaa;
  font-size: 0.85rem;
  line-height: 1.7;
}

/* Logo */
.footer-logo {
  display: flex;
  align-items: center;
  justify-content: center;
}

.footer-logo-img {
  height: 70px;
  width: auto;
  opacity: 0.85;
  transition: all 0.3s ease;
  filter: drop-shadow(0 0 10px rgba(0, 212, 255, 0.2));
}

.footer-logo-img:hover {
  opacity: 1;
  filter: drop-shadow(0 0 15px rgba(0, 212, 255, 0.4));
}

/* Contact */
.footer-contact {
  text-align: right;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.contact-email {
  color: #e0e0e0;
  font-size: 0.95rem;
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 500;
}

.contact-email:hover {
  color: var(--color-primary);
}

.social-links {
  display: flex;
  gap: 10px;
}

.social-icon {
  width: 36px;
  height: 36px;
  border: 1px solid #555;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ccc;
  transition: all 0.2s;
}

.social-icon:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.social-icon svg {
  width: 16px;
  height: 16px;
}

/* Bottom Bar */
.footer-bottom {
  padding: 20px 60px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.3);
}

.footer-bottom-inner {
  max-width: var(--container-max);
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.copyright {
  color: #999;
  font-size: 0.8rem;
  max-width: 600px;
  line-height: 1.5;
}

.footer-bottom-right {
  display: flex;
  align-items: center;
  gap: 30px;
}

.legal-links {
  display: flex;
  gap: 20px;
}

.legal-links a {
  color: #bbb;
  font-size: 0.8rem;
  text-decoration: none;
  transition: color 0.2s;
  font-weight: 500;
}

.legal-links a:hover {
  color: var(--color-primary);
}

.payment-icons {
  display: flex;
  align-items: center;
  margin-left: auto;
}

/* Keep all logos together: the group never splits, so they wrap as one block
   (not 3 + 1) when the row runs out of horizontal space. */
.payment-logos-group {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;
}

.payment-img {
  height: 28px;
  width: auto;
  object-fit: contain;
}

@media (max-width: 968px) {
  .footer-main {
    padding: 30px 20px;
  }
  
  .footer-inner {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 30px;
  }
  
  .footer-company,
  .footer-contact {
    text-align: center;
    align-items: center;
  }
  
  .footer-bottom {
    padding: 20px;
  }
  
  .footer-bottom-inner {
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .copyright {
    max-width: 100%;
  }
  
  .footer-bottom-right {
    flex-direction: column;
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .footer-main {
    padding: 25px 15px;
  }
  
  .footer-inner {
    gap: 25px;
  }
  
  .company-name {
    font-size: 1rem;
  }
  
  .company-detail {
    font-size: 0.75rem;
  }
  
  .logo-text {
    font-size: 1.5rem;
  }
  
  .contact-email {
    font-size: 0.85rem;
  }
  
  .social-icon {
    width: 36px;
    height: 36px;
  }
  
  .footer-bottom {
    padding: 15px 12px;
  }
  
  .copyright {
    font-size: 0.7rem;
    line-height: 1.4;
  }
  
  .legal-links {
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  }
  
  .legal-links a {
    font-size: 0.75rem;
  }
  
  .payment-img {
    height: 24px;
  }
}
</style>
